<script lang="ts">
import { provide } from 'vue';

import type { InjectionKey } from 'vue';

export type PhxsgAccordionType = 'single' | 'multiple';

export type PhxsgAccordionValue = string | string[] | null;

export interface PhxsgAccordionContext {
  boxed: boolean;
  isOpen: (id: string) => boolean;
  toggle: (id: string) => void;
  close: (id: string) => void;
}

export const PhxsgAccordionKey: InjectionKey<PhxsgAccordionContext> = Symbol('PhxsgAccordion');

export interface PhxsgAccordionProps {
  type?: PhxsgAccordionType;
  boxed?: boolean;
}
</script>

<script setup lang="ts">
const { type = 'single', boxed = false } = defineProps<PhxsgAccordionProps>();

const model = defineModel<PhxsgAccordionValue>({ default: null });

function openIds(): string[] {
  if (Array.isArray(model.value)) {
    return model.value;
  }

  return model.value ? [model.value] : [];
}

function isOpen(id: string): boolean {
  return openIds().includes(id);
}

function toggle(id: string): void {
  if (type === 'single') {
    model.value = isOpen(id) ? null : id;
    return;
  }

  model.value = isOpen(id) ? openIds().filter((openId) => openId !== id) : [...openIds(), id];
}

function close(id: string): void {
  if (!isOpen(id)) {
    return;
  }

  model.value = type === 'single' ? null : openIds().filter((openId) => openId !== id);
}

provide(PhxsgAccordionKey, { boxed, isOpen, toggle, close });
</script>

<template>
  <ul class="phxsg-accordion" :class="{ 'phxsg-accordion--boxed': boxed }">
    <slot />
  </ul>
</template>

<style src="./accordion.css" />
